import { effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { GbrUser, User, LanguageCode, TokenClaims } from '@gbr-monorepo/interfaces';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { SetUser, SetUserLanguage } from '../state/user.action';
import { UserManagementService } from '../../user-management/services/user-management.service';
import { getEnumValue } from '@gbr-monorepo/backend-lib/helpers';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly userService = inject(UserManagementService);
    private readonly store$ = inject(Store);
    private readonly router = inject(Router);

    private readonly msalService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);

    private readonly inProgress = toSignal(this.msalBroadcastService.inProgress$);

    private readonly _isAuthenticated = signal(false);
    public readonly isAuthenticated = this._isAuthenticated.asReadonly();

    constructor() {
        effect(async () => {
            const status = this.inProgress();
            if (status === InteractionStatus.None) {
                await this.handleLogin();
            }
        });
    }

    public logout(): void {
        this.msalService.logoutRedirect();
    }

    private async handleLogin(): Promise<void> {
        const activeAccount = await this.getActiveAccount();
        const idTokenClaims = activeAccount?.idTokenClaims as TokenClaims | undefined;
        if (idTokenClaims && !!idTokenClaims?.['siemens_gid']) {
            await this.loadGbrUserAndSetState(idTokenClaims);
            this._isAuthenticated.set(true);
        } else {
            await this.router.navigate(['/auth/unauthorized']);
        }
    }

    private async getActiveAccount(): Promise<AccountInfo | undefined> {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length === 0) {
            return undefined;
        }
        const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];
        if (!activeAccount) {
            return undefined;
        }
        this.msalService.instance.setActiveAccount(activeAccount);
        return activeAccount;
    }

    private async loadGbrUserAndSetState(idTokenClaims: TokenClaims): Promise<void> {
        try {
            const user = await firstValueFrom(this.userService.getUser(idTokenClaims.siemens_gid));
            const gbrUser = this.mapUserToGbrUser(idTokenClaims, user);
            const languageCode = getEnumValue(LanguageCode, user.language);
            await this.dispatchStoreData(true, gbrUser, languageCode);
        } catch (error: unknown) {
            console.error(error);
            await this.router.navigate(['/auth/unauthorized']);
        }
    }

    private dispatchStoreData(isAuthenticated: boolean, user: GbrUser, language: LanguageCode): Promise<unknown> {
        return firstValueFrom(
            this.store$.dispatch([
                new SetUser({
                    isAuthenticated,
                    user
                }),
                new SetUserLanguage({ language })
            ])
        );
    }

    private mapUserToGbrUser(authUser: TokenClaims, user: User): GbrUser {
        return {
            gid: authUser?.siemens_gid,
            firstName: authUser?.given_name,
            lastName: authUser?.family_name,
            email: authUser?.email,
            orgCode: user?.orgCode,
            roles: user?.roles
        } as GbrUser;
    }
}
